import ApiService from '@/services/apiService';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import { mapState, mapGetters } from 'vuex';
import { themesMixin, vuetifyColors } from '@/utils/themes.js';
import { gatewayMgmtMixin, userItemsPerListMixin, isCopyToClipboardSupported, copyToClipboard } from '@/utils/vuex';
import { localizeDate } from '@/utils/date';
import listMixin from '../listMixin';
import listOptionsPersistenceMixin from '@/components/custom/listOptionsPersistenceMixin';
const defaultItemsPerPage = 10;
const defaultTableOptions = {
  sortBy: ['id'],
  sortDesc: [false],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
export default {
  name: 'NiotaClassicGwMgmtList',
  mixins: [gatewayMgmtMixin, themesMixin, listMixin, listOptionsPersistenceMixin, userItemsPerListMixin],
  props: {
    accountId: {
      type: Number,
      required: false,
      default: undefined
    },
    filterWarning: {
      type: Boolean,
      required: false,
      default: false
    },
    filterAlert: {
      type: Boolean,
      required: false,
      default: false
    },
    editable: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data: function () {
    return {
      isFetching: false,
      tableOptions: {
        ...defaultTableOptions
      },
      defaultTableOptions: {
        ...defaultTableOptions
      },
      priorRequestOptions: null,
      serverItemsLength: 0,
      gateways: [],
      expandedItems: [],
      deletePrompt: false,
      selectedItem: {},
      disableFetchOnCreatedMixin: true,
      searchItem: null,
      selectedAccount: null,
      listPersistencePath: 'GWMgmt.list',
      searchMode: 'single',
      accountsMode: 'single',
      includeSubAccounts: true
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions', 'refreshGMMinutes']),
    ...mapGetters({
      sessionUserId: 'authentication/userId'
    }),
    isIotHubManager() {
      return this.permissions['IotHub.manage'];
    },
    isAdmin() {
      return this.permissions['all.manage'];
    },
    tableLocalizations() {
      return {
        headers: [{
          sortable: false
        }, {
          text: this.$i18n_t('NiotaClassic.Common.table.id'),
          value: 'id',
          align: 'right'
        }, {
          text: this.$i18n_t('NiotaClassic.Common.table.title'),
          value: 'title'
        }, {
          text: this.$i18n_t('NiotaClassic.GWMonitoring.gwEui'),
          value: 'eui'
        }, {
          text: this.$i18n_t('NiotaClassic.GWMonitoring.status'),
          value: 'status',
          sortable: false
        }, {
          text: this.$i18n_t('NiotaClassic.DeviceMonitoring.lastPacket'),
          value: 'createdAt',
          sortable: false
        }, {
          text: this.$i18n_t('NiotaClassic.Devices.table.rssi'),
          value: 'rssi',
          sortable: false
        }, {
          text: this.$i18n_t('NiotaClassic.GWMonitoring.account'),
          value: 'accountTitle',
          sortable: true
        }, {
          text: this.$i18n_t('NiotaClassic.GWMonitoring.form.group'),
          value: 'group',
          sortable: true
        }, {
          sortable: false
        }],
        tableFooterProps: {
          itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
          itemsPerPageOptions: [defaultItemsPerPage, 20, 50]
        }
      };
    }
  },
  watch: {
    accountId: debounce(function () {
      // Items have already been requested
      if (this.isFetching) {
        return;
      }
      this.fetchList();
    }, 500),
    filterWarning: debounce(function () {
      // Items have already been requested
      if (this.isFetching) {
        return;
      }
      this.fetchList();
    }, 500),
    filterAlert: debounce(function () {
      // Items have already been requested
      if (this.isFetching) {
        return;
      }
      this.fetchList();
    }, 500),
    includeSubAccounts: debounce(function () {
      // Items have already been requested
      if (this.isFetching) {
        return;
      }
      this.fetchList();
    }, 500),
    refreshGMMinutes(newValue) {
      if (this.isFetching) {
        return;
      }
      this.clearGMInterval(this.intervalId);
      if (newValue > 0) {
        let seconds = this.convertMinutesToSeconds(newValue);
        this.intervalId = setInterval(() => {
          this.fetchList();
        }, seconds * 1000);
      }
    }
  },
  beforeDestroy() {
    this.clearGMInterval(this.intervalId);
  },
  methods: {
    initializedDefaultPageFilters() {
      const filterWarning = get(this.$store.state.app, `pageFilters.${this.listPersistencePath}.filterWarning`) || false;
      const filterAlert = get(this.$store.state.app, `pageFilters.${this.listPersistencePath}.filterAlert`) || false;
      this.includeSubAccounts = get(this.$store.state.app, `pageFilters.${this.listPersistencePath}.includeSubAccounts`, true) || false;
      // parent should trigger a `fetchList` in @loaded-config
      this.$emit('loaded-config', {
        accountId: this.selectedAccount,
        filterWarning,
        filterAlert
      });
    },
    fetchList() {
      if (this.isFetching) {
        return;
      }
      this.isFetching = true;
      const searchItem = (this.searchItem || '').trim().length > 0 ? this.searchItem.trim() : undefined;
      if (get(this.priorRequestOptions, 'search') !== searchItem) {
        // if the search-item is changed, reset to page 1
        this.tableOptions = {
          ...this.tableOptions,
          page: 1
        };
      }
      const {
        sortBy,
        sortDesc,
        page,
        itemsPerPage
      } = this.tableOptions;
      const sortItem = sortBy && sortBy.length ? sortBy[0] : 'id';
      const doSortDesc = sortDesc && sortDesc.length ? sortDesc[0] : true;
      const sortDirection = doSortDesc ? 'DESC' : 'ASC';
      const usedPage = Math.max(1, page || 1);
      const usedItemsPerPage = itemsPerPage || defaultItemsPerPage;
      const offset = (usedPage - 1) * usedItemsPerPage;
      const newRequestOptions = {
        accountId: this.accountId,
        offset,
        limit: usedItemsPerPage,
        order: {
          variable: sortItem,
          direction: sortDirection
        },
        search: searchItem,
        options: [this.filterWarning ? 'warning' : null, this.filterAlert ? 'alert' : null].filter(o => o),
        includeSubAccounts: this.includeSubAccounts
      };

      // persist options for next call of this page (during this browser-tab-session)
      this.$store.state.app.pageFilters = {
        ...this.$store.state.app.pageFilters,
        GWMgmt: {
          ...(get(this.$store.state.app, 'pageFilters.GWMgmt') || {}),
          list: {
            searchDetails: this.searchItem,
            tableOptions: this.tableOptions,
            includeSubAccounts: this.includeSubAccounts,
            accountFilter: this.accountId,
            filterWarning: this.filterWarning,
            filterAlert: this.filterAlert
          }
        }
      };
      this.priorRequestOptions = newRequestOptions;
      ApiService.gwMgmtFetch(newRequestOptions.accountId, newRequestOptions.offset, newRequestOptions.limit, newRequestOptions.order, newRequestOptions.options, newRequestOptions.search, newRequestOptions.includeSubAccounts).then(response => {
        this.gateways = response.gateways;
        this.serverItemsLength = response.totalCount;
        this.$emit('refreshed-list');
      }).catch(error => {
        this.gateways = [];
        this.$snack(this.$i18n_t('NiotaClassic.GWMonitoring.snacks.errorGatewayDevice'), 'error');
        console.error(error);
      }).finally(() => {
        this.isFetching = false;
      });
    },
    localizeDate(item) {
      if (item) {
        return localizeDate(item);
      }
    },
    openDetails(item) {
      this.$router.push(`/niota-classic/gw-mgmt/${item.id}/details`);
    },
    isCopySupported() {
      return isCopyToClipboardSupported();
    },
    copyToClipboard(event, copyValue) {
      event.stopPropagation();
      copyToClipboard(copyValue);
    },
    lastPacket(item) {
      return this.localizeDate(get(item, 'lastPacket._source.received_at_server'));
    },
    getRssi(item) {
      return get(item, 'lastPacket._source.rssi');
    },
    getAccount(item) {
      return get(item, 'account.name');
    },
    getGroup(item) {
      return get(item, 'properties.group');
    },
    deleteItem() {
      return ApiService.gwMgmtDelete(this.selectedItem.id).then(() => {
        this.$snack(this.$i18n_t('NiotaClassic.GWMonitoring.snacks.deleteSuccess'), 'success');
        this.deletePrompt = false;
        this.fetchList();
      }).catch(error => {
        this.$snack(this.$i18n_t('NiotaClassic.GWMonitoring.snacks.deleteFailed'), 'error');
        console.error(error);
      });
    },
    showPrompt(item) {
      this.selectedItem = item;
      this.deletePrompt = true;
    },
    getColor(type) {
      return vuetifyColors[type];
    },
    getState(item) {
      const lastPaket = get(item, 'lastPacket._source.received_at_server');
      const green = parseFloat(get(item, 'properties.threshold.green') || 5);
      const yellow = parseFloat(get(item, 'properties.threshold.yellow') || 12);
      // 36e5 aka 1 hr in ms
      const diff = Math.abs(new Date(lastPaket) - new Date()) / 36e5;
      if (diff < green) {
        return this.getColor('success');
      } else if (diff >= green && diff < yellow) {
        return this.getColor('warning');
      } else {
        return this.getColor('error');
      }
    }
  }
};